import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import type { PinState } from '../model/link'

/**
 * A placement's pin state, and — where a caller supplies `onClick` — the
 * way to change it.
 *
 * A publication freezes the whole composed tree. A placement pin has a
 * narrower job: it holds one edge at an older target revision when a
 * future release is captured. Unpinned edges follow the target revision
 * capture sees; they do not make an authored edit public by themselves.
 *
 * An **inline** child has no pin state at all — no identity or separate
 * stream. Where clickable, its chip offers to extract it into a component
 * of its own; only then can it resolve to latest or be pinned.
 */
export function PinChip({ pin, onClick }: { pin: PinState | undefined; onClick?: () => void }) {
  if (!pin) return null

  if (pin.kind === 'inline') {
    const clickable = onClick
      ? {
          onClick: (event: React.MouseEvent) => {
            event.stopPropagation()
            onClick()
          },
          clickable: true as const,
        }
      : {}
    return (
      <Tooltip title={onClick ? 'Defined in place. Click to extract it into a component of its own.' : 'Defined in place. It versions with this document.'}>
        <Chip size="small" variant="outlined" label="inline" {...clickable} />
      </Tooltip>
    )
  }

  const clickable = onClick
    ? {
        onClick: (event: React.MouseEvent) => {
          // The row underneath selects; the chip changes a pin. Without
          // this, pinning would also move the selection.
          event.stopPropagation()
          onClick()
        },
        clickable: true as const,
      }
    : {}

  if (pin.kind === 'pinned') {
    return (
      <Tooltip
        title={
          onClick
            ? `Held at revision ${pin.revision}. Click to move or release the hold.`
            : `Held at revision ${pin.revision} in future releases until this hold moves.`
        }
      >
        <Chip size="small" color="primary" variant="outlined" label={`held r${pin.revision}`} {...clickable} />
      </Tooltip>
    )
  }

  return (
    <Tooltip
      title={
        onClick
          ? "Follows the target revision captured by the next release. Click to hold an earlier revision."
          : "Follows the target revision captured by each release."
      }
    >
      <Chip size="small" variant="outlined" label="follows current" {...clickable} />
    </Tooltip>
  )
}
